<template>
  <div class="min-h-screen flex flex-col" style="background: var(--color-bg); color: var(--color-text);">

    <!-- ── Loading ──────────────────────────────────────────────────────── -->
    <div v-if="state === 'loading'" class="flex-1 flex items-center justify-center">
      <div class="flex flex-col items-center gap-3">
        <div class="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style="border-color: var(--color-primary); border-top-color: transparent;"></div>
        <span style="color: var(--color-muted); font-size: .875rem;">{{ $t('common.loading') }}</span>
      </div>
    </div>

    <!-- ── Not found ─────────────────────────────────────────────────────── -->
    <div v-else-if="state === 'not_found'" class="flex-1 flex flex-col items-center justify-center gap-6 px-4">
      <img src="/eqsys-white.svg" alt="eqSys" class="h-10 w-auto" />
      <div class="max-w-sm w-full p-6 rounded-xl text-center" style="background: var(--color-surface); border: 1px solid var(--color-border);">
        <div class="text-4xl font-bold mb-2" style="color: var(--color-primary);">404</div>
        <div class="text-lg font-semibold mb-1">{{ $t('ui.linkNotFound') }}</div>
        <p style="color: var(--color-muted); font-size: .875rem;">{{ $t('ui.thisSigningLinkIsInvalidOrDoesNotExistPleaseCheckTheLinkInYourEmail') }}</p>
      </div>
    </div>

    <!-- ── Invalid (signed / expired / cancelled) ────────────────────────── -->
    <div v-else-if="state === 'invalid'" class="flex-1 flex flex-col items-center justify-center gap-6 px-4">
      <img src="/eqsys-white.svg" alt="eqSys" class="h-10 w-auto" />
      <div class="max-w-sm w-full p-6 rounded-xl text-center" style="background: var(--color-surface); border: 1px solid var(--color-border);">
        <component :is="invalidIcon" class="w-10 h-10 mx-auto mb-3" :style="{ color: invalidColor }" />
        <div class="text-lg font-semibold mb-1">{{ invalidTitle }}</div>
        <p style="color: var(--color-muted); font-size: .875rem;">{{ invalidMessage }}</p>
      </div>
    </div>

    <!-- ── Step 1: Review ────────────────────────────────────────────────── -->
    <template v-else-if="state === 'step_review'">
      <!-- Header -->
      <div class="px-4 py-3 flex items-center gap-3 border-b shrink-0" style="background: var(--color-bg); border-color: var(--color-border);">
        <img src="/eqsys-white.svg" alt="eqSys" class="h-7 w-auto" />
        <div class="flex-1 min-w-0">
          <div class="font-semibold truncate text-sm">{{ meta.document_title }}</div>
          <div class="text-xs truncate" style="color: var(--color-muted);">{{ meta.company_name }}</div>
        </div>
      </div>

      <!-- Context -->
      <div class="px-4 py-3 flex flex-wrap gap-x-4 gap-y-1 shrink-0 border-b text-sm" style="border-color: var(--color-border);">
        <span><span style="color: var(--color-muted);">{{ $t('ui.project') }}</span> {{ meta.project_name }}</span>
        <span><span style="color: var(--color-muted);">{{ $t('ui.signer') }}</span> {{ meta.signer_name }}</span>
        <span v-if="meta.expires_at"><span style="color: var(--color-muted);">{{ $t('ui.expires') }}</span> {{ formatExpiry(meta.expires_at) }}</span>
      </div>

      <!-- PDF preview area -->
      <div class="flex-1 flex flex-col min-h-0 px-4 py-3 gap-2">
        <p class="text-sm shrink-0" style="color: var(--color-muted);">{{ $t('ui.pleaseReviewTheContractBelowBeforeSigning') }}</p>
        <div class="flex-1 rounded-lg overflow-hidden border min-h-0" style="border-color: var(--color-border);">
          <iframe
            :src="previewUrl"
            class="w-full h-full min-h-[55vh] lg:min-h-[65vh]"
            style="border: none; background: #fff;"
            :title="$t('ui.contractPdf')"
          ></iframe>
        </div>
        <!-- iOS / mobile fallback -->
        <a
          :href="previewUrl"
          target="_blank"
          rel="noopener"
          class="text-xs text-center"
          style="color: var(--color-primary);"
        >{{ $t('ui.openPdfInNewTab') }}</a>
      </div>

      <!-- Sticky CTA -->
      <div class="px-4 py-4 border-t shrink-0 flex justify-end" style="background: var(--color-bg); border-color: var(--color-border);">
        <button class="btn primary" @click="state = 'step_sign'">
          {{ $t('ui.proceedToSign') }}
        </button>
      </div>
    </template>

    <!-- ── Step 2: Sign ──────────────────────────────────────────────────── -->
    <template v-else-if="state === 'step_sign'">
      <!-- Header -->
      <div class="px-4 py-3 flex items-center gap-3 border-b shrink-0" style="background: var(--color-bg); border-color: var(--color-border);">
        <button class="btn-back" @click="state = 'step_review'" :title="$t('ui.backToReview')">
          <MdiArrowLeft class="w-5 h-5" />
        </button>
        <div class="flex-1 min-w-0">
          <div class="font-semibold truncate text-sm">{{ $t('ui.signDocument') }}</div>
          <div class="text-xs truncate" style="color: var(--color-muted);">{{ meta.signer_name }}</div>
        </div>
      </div>

      <div class="flex-1 flex flex-col px-4 py-4 gap-4">
        <p class="text-sm" style="color: var(--color-muted);">{{ $t('ui.drawYourSignatureBelowUsingYourFingerOrMouse') }}</p>

        <!-- Canvas wrapper -->
        <div class="rounded-lg overflow-hidden border-2" :style="{ borderColor: canvasEmpty ? 'var(--color-border)' : 'var(--color-primary)' }">
          <SignatureCanvas
            ref="canvasRef"
            :height="200"
            @change="onCanvasChange"
          />
        </div>

        <!-- Error -->
        <div v-if="submitError" class="text-sm px-3 py-2 rounded-lg" style="background: rgba(239,68,68,0.1); color: #f87171; border: 1px solid rgba(239,68,68,0.25);">
          {{ submitError }}
        </div>
      </div>

      <!-- Action row -->
      <div class="px-4 py-4 border-t shrink-0 flex items-center gap-2" style="background: var(--color-bg); border-color: var(--color-border);">
        <button class="btn ghost small" @click="canvasRef?.clear()" :disabled="canvasEmpty">{{ $t('common.clear') }}</button>
        <span class="flex-1" />
        <button
          class="btn primary"
          :disabled="canvasEmpty || submitting"
          @click="submitSig"
        >
          <span v-if="submitting" class="flex items-center gap-2">
            <span class="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin inline-block" style="border-color: white; border-top-color: transparent;"></span>
            {{ $t('maintenance.submitting') }}
          </span>
          <span v-else>{{ $t('ui.submitSignature') }}</span>
        </button>
      </div>
    </template>

    <!-- ── Success ───────────────────────────────────────────────────────── -->
    <div v-else-if="state === 'success'" class="flex-1 flex flex-col items-center justify-center gap-6 px-4">
      <img src="/eqsys-white.svg" alt="eqSys" class="h-10 w-auto" />
      <div class="max-w-sm w-full p-6 rounded-xl text-center" style="background: var(--color-surface); border: 1px solid var(--color-border);">
        <MdiCheckCircle class="w-12 h-12 mx-auto mb-3" style="color: var(--color-accent);" />
        <div class="text-lg font-semibold mb-1">{{ $t('ui.documentSigned') }}</div>
        <p style="color: var(--color-muted); font-size: .875rem;">
          {{ $t('ui.signedConfirmation', { date: signedAt, email: meta?.signer_email }) }}
        </p>
      </div>
    </div>

  </div>
</template>

<script setup>
const { t, locale } = useI18n()
import { useI18n } from 'vue-i18n'

import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import MdiArrowLeft    from '~icons/mdi/arrow-left'
import MdiCheckCircle  from '~icons/mdi/check-circle'
import MdiClockAlert   from '~icons/mdi/clock-alert'
import MdiCancel       from '~icons/mdi/cancel'
import MdiCheckAll     from '~icons/mdi/check-all'
import SignatureCanvas from '@/components/base/SignatureCanvas.vue'
import { getSigningPage, getSignPreviewUrl, submitSignature } from '@/api/api.js'

const route = useRoute()
const token = route.params.token

// ── State ──────────────────────────────────────────────────────────────────
const state       = ref('loading') // loading | not_found | invalid | step_review | step_sign | success
const meta        = ref(null)
const previewUrl  = ref('')
const canvasRef   = ref(null)
const canvasEmpty = ref(true)
const submitting  = ref(false)
const submitError = ref('')
const signedAt    = ref('')

// ── Load ───────────────────────────────────────────────────────────────────
onMounted(async () => {
  try {
    const data = await getSigningPage(token)
    meta.value = data

    if (!data.is_valid || data.status !== 'pending') {
      state.value = 'invalid'
      return
    }

    previewUrl.value = getSignPreviewUrl(token)
    state.value = 'step_review'
  } catch (e) {
    if (e?.status === 404) {
      state.value = 'not_found'
    } else {
      // treat unexpected errors as not found
      state.value = 'not_found'
    }
  }
})

// ── Invalid state helpers ──────────────────────────────────────────────────
const invalidIcon = computed(() => {
  const s = meta.value?.status
  if (s === 'signed')    return MdiCheckAll
  if (s === 'expired')   return MdiClockAlert
  return MdiCancel
})
const invalidColor = computed(() => {
  const s = meta.value?.status
  if (s === 'signed')  return 'var(--color-accent)'
  if (s === 'expired') return '#f59e0b'
  return '#f87171'
})
const invalidTitle = computed(() => {
  const s = meta.value?.status
  if (s === 'signed')    return t('ui.alreadySigned')
  if (s === 'expired')   return t('ui.linkExpired')
  if (s === 'cancelled') return t('ui.requestCancelled')
  return t('ui.linkUnavailable')
})
const invalidMessage = computed(() => {
  const s = meta.value?.status
  if (s === 'signed')    return t('ui.thisDocumentHasAlreadyBeenSignedCheckYourEmailForTheSignedCopy')
  if (s === 'expired')   return t('ui.thisSigningLinkHasExpiredPleaseContactTheSenderToRequestANewOne')
  if (s === 'cancelled') return t('ui.thisSigningRequestHasBeenCancelledPleaseContactTheSenderForMoreInformation')
  return t('ui.thisSigningLinkIsNoLongerValid')
})

// ── Helpers ────────────────────────────────────────────────────────────────
function formatExpiry(iso) {
  return new Date(iso).toLocaleDateString(locale.value, { day: 'numeric', month: 'short', year: 'numeric' })
}

function onCanvasChange(isEmpty) {
  canvasEmpty.value = isEmpty
}

// ── Submit ─────────────────────────────────────────────────────────────────
async function submitSig() {
  if (canvasEmpty.value || submitting.value) return
  submitting.value = true
  submitError.value = ''
  try {
    const data = canvasRef.value.getImageData()
    await submitSignature(token, data)
    signedAt.value = new Date().toLocaleDateString(locale.value, { day: 'numeric', month: 'long', year: 'numeric' })
    state.value = 'success'
  } catch (e) {
    submitError.value = e?.data?.detail ?? e?.data?.signature_data ?? e?.message ?? t('ui.submissionFailedPleaseTryAgain')
  } finally {
    submitting.value = false
  }
}
</script>
